/* =============================================================================
   vp-video-mobile.js — leichtere Videoquelle, frueherer Start   (05.09.2026)

   Ergaenzt vp-video-mobile.css. Die CSS-Datei nimmt den schwarzen Block weg,
   diese Datei macht das Laden selbst schneller.

   ---------------------------------------------------------------------------
   BEFUND (mobil in der Vorschau gemessen, 375px)

   1) Alle vier Videosektionen rendern `video_tag: ..., preload: 'none'`. Der
      Browser laedt damit waehrend des Seitenaufbaus NICHTS. Erst global.js
      startet die Videos:

        setTimeout(() => { ...querySelectorAll('video').forEach(v => v.play()) }, 10)

      global.js haengt als defer-Script in Zeile 55 der theme.liquid und
      laeuft damit erst nach dem kompletten Parsen. Gemessen auf der
      Startseite: DOMContentLoaded bei 5456 ms — so lange lag das Video
      unberuehrt da.

   2) `video_tag` gibt genau EINE Quelle aus, und zwar die groesste:

        <source src=".../<hash>.HD-1080p-7.2Mbps-<id>.mp4" type="video/mp4">

      Gemessen wurden dafuer 13.006.538 Bytes. Auch mobil. Shopify haelt zu
      derselben Datei bereit (per Range-Request geprueft):

        SD-480p-1.5Mbps    2.864.674 Bytes
        HD-720p-4.5Mbps    8.220.773 Bytes
        HD-1080p-7.2Mbps  13.006.538 Bytes

      Die Bitrate im Dateinamen ist je Video anders und laesst sich NICHT
      raten (`...SD-480p-1.2Mbps-...` liefert 404). Ableitbar ist dagegen die
      HLS-Playlist: alles nach `<hash>.` durch `m3u8` ersetzen. Sie ist 644
      Bytes gross und nennt die exakten Namen aller Stufen:

        #EXT-X-STREAM-INF:...,RESOLUTION=1280x720,...
        <hash>.HD-720p.4.5Mbps.hls.<id>_.m3u8

   ---------------------------------------------------------------------------
   AENDERUNG

   Ein MutationObserver greift jedes <video> in einer .lazy-video-Figur schon
   beim Parsen ab — also lange vor den defer-Scripts.

   Mobil (<= 768px) wird die Playlist geholt (644 Bytes), die passende Stufe
   daraus gelesen und die mp4-URL zusammengesetzt: 720p fuer die bildschirm-
   fuellenden Videos (Header-Slideshow, Video-Banner, erkennbar an
   .lazy-image--absolut), 480p fuer die kleineren (Medien mit Text, Medien
   mit Tabs). Bewusst nicht ueberall 480p: die Videos sind 16:9 und stehen im
   Header in einem hochkanten Ausschnitt, da beschneidet der Bildausschnitt
   den Grossteil der Breite weg. Faellt der Abruf aus oder laesst sich die
   Stufe nicht abspielen, bleibt die Quelle des Themes stehen.

   Bewusst KEIN HLS, obwohl die m3u8 hier direkt danebenliegt: getestet
   meldet Chromium auf Android `canPlayType('application/vnd.apple.mpegurl')`
   = "maybe", nimmt die Playlist an und spielt sie dann doch nicht — das
   Video blieb bei currentTime 0 stehen. Es gibt keinen verlaesslichen Test
   auf natives HLS, also fuer alle mobilen Browser derselbe mp4-Weg.

   Desktop bleibt bei der Quelle des Themes — dort ist 1080p richtig.

   Laden und Abspielen sind getrennt. Geladen wird sofort — das ist der
   Zeitgewinn. Abgespielt wird erst nach DOMContentLoaded, und zwar in bis zu
   fuenf Anlaeufen ueber rund drei Sekunden:

   global.js geht kurz nach dem Start einmal ueber alle Videos und ruft fuer
   Slideshow-Videos `pause()`, solange deren Slide noch nicht `.current` ist.
   Solange das Video nichts geladen hatte, lief dieses `pause()` ins Leere.
   Jetzt, wo es frueh Daten hat, greift es — und danach spielt es niemand
   mehr an, bis der Besucher scrollt. Gemessen blieb der Header dann bei
   currentTime 0 stehen. `ensurePlaying` faengt genau das ab und haelt sich
   dabei an dieselbe Slide-Bedingung wie global.js.

   Auf der Kinder-Vorlage kam ein zweites `pause()` noch spaeter, bei rund
   5,8 s — das Video blieb dann als Standbild stehen. Deshalb zusaetzlich
   `keepPlaying`: ein dauerhafter pause-Listener, der wieder anspielt, wenn
   das Video im Blick ist und seine Slide die aktuelle ist. Auf 20 Anlaeufe
   gedeckelt, damit daraus kein Schlagabtausch mit dem Theme wird. Scrollt
   der Besucher weg, greift er nicht — dann hat global.js recht.

   Ueberall gilt:
     * Das erste Video der Seite laedt sofort. Alle weiteren starten ueber
       einen IntersectionObserver 800px bevor sie in den Blick kommen,
       statt erst wenn sie schon sichtbar sind.
     * `lazyloaded` wird bereits bei `loadedmetadata` gesetzt statt erst bei
       `canplay`.
     * Videos in .none-autoplay (Medien mit Tabs) bekommen nur die passende
       Quelle. Ob und wann sie laufen, entscheidet weiter das Theme.
     * Das Posterbild kommt vom Theme mit image_size "500x". Auf einem
       bildschirmfuellenden Header ist das sichtbar unscharf. Es wird durch
       eine breitere Variante derselben Datei ersetzt, sobald diese geladen
       ist — und nur, solange das Poster ueberhaupt noch zu sehen ist.

   GELTUNGSBEREICH: jede .lazy-video-Figur, also die vier Sektionen oben auf
   allen Seiten. Videos ausserhalb dieser Figuren (Produktgalerie, Karten)
   werden nicht angefasst.

   ZURUECKNEHMEN: die Zeile fuer diese Datei in snippets/custom-assets.liquid
   entfernen.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.MutationObserver || !document.documentElement.closest) return;

  var DONE = 'vpVideoHandled';
  var MOBILE_MAX = 768;
  var EAGER_LIMIT = 1; // so viele Videos in Dokumentreihenfolge sofort laden
  var eagerUsed = 0;

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  function posterWidth() {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    return Math.min(1600, Math.max(800, Math.round(window.innerWidth * dpr)));
  }

  function firstMp4(video) {
    var nodes = video.querySelectorAll('source');
    for (var i = 0; i < nodes.length; i++) {
      var src = nodes[i].getAttribute('src') || '';
      if (src.indexOf('.mp4') > -1) return src;
    }
    return null;
  }

  // .../<hash>/<hash>.HD-1080p-7.2Mbps-<id>.mp4?v=0  ->  .../<hash>/<hash>.m3u8
  function deriveM3u8(mp4Url) {
    var m = mp4Url.match(/^(.*\/[0-9a-f]{8,}\.)[^/]+\.mp4(?:\?.*)?$/);
    return m ? m[1] + 'm3u8' : null;
  }

  // Aus der Playlist die Stufe >= targetHeight holen, sonst die groesste.
  // Rueckgabe z.B. "HD-720p-4.5Mbps" — so heisst der Abschnitt in der mp4-URL.
  function pickRendition(playlist, targetHeight) {
    var lines = playlist.split('\n');
    var found = [];
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].indexOf('#EXT-X-STREAM-INF') !== 0) continue;
      var res = lines[i].match(/RESOLUTION=\d+x(\d+)/);
      var path = (lines[i + 1] || '').trim();
      var tag = path.match(/\.([A-Z]{2}-\d+p)\.([\d.]+Mbps)\.hls\./);
      if (!res || !tag) continue;
      found.push({ h: parseInt(res[1], 10), tag: tag[1] + '-' + tag[2] });
    }
    if (!found.length) return null;
    found.sort(function (a, b) { return a.h - b.h; });
    for (var j = 0; j < found.length; j++) {
      if (found[j].h >= targetHeight) return found[j].tag;
    }
    return found[found.length - 1].tag;
  }

  function swapRendition(mp4Url, tag) {
    var re = /\.[A-Z]{2}-\d+p-[\d.]+Mbps-/;
    return re.test(mp4Url) ? mp4Url.replace(re, '.' + tag + '-') : null;
  }

  function upsizePoster(url, width) {
    if (!url) return null;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    if (/_\d+x(\.[a-zA-Z]+)/.test(url)) return url.replace(/_\d+x(\.[a-zA-Z]+)/, '_' + width + 'x$1');
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function sharpenPoster(video) {
    var current = video.getAttribute('poster');
    if (!current) return;
    var wide = upsizePoster(current, posterWidth());
    if (!wide || wide === current) return;
    var probe = new Image();
    probe.onload = function () {
      // Nur tauschen, solange das Poster ueberhaupt noch zu sehen ist.
      if (video.readyState < 2) video.setAttribute('poster', wide);
    };
    probe.src = wide;
  }

  function tryPlay(video) {
    var p = video.play();
    if (p && p.catch) p.catch(function () { /* Autoplay abgelehnt: Poster bleibt */ });
  }

  function whenDomReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function inView(el) {
    var b = el.getBoundingClientRect();
    return b.bottom > 0 && b.top < (window.innerHeight || 0) && b.width > 0;
  }

  // global.js pausiert Videos, deren Slide (noch) nicht die aktuelle ist.
  // Dieselbe Bedingung, damit wir ihm nicht in die Quere kommen.
  function slideAllowsPlay(video) {
    if (!video.closest('.slideshow') && !video.closest('.shoppable-media-slider')) return true;
    return !!(video.closest('.current') || video.closest('.swiper-slide-active'));
  }

  // Anlauf: bis zu fuenf Versuche ueber rund drei Sekunden.
  function ensurePlaying(video, attempt) {
    if (attempt > 4) return;
    if (video.paused && inView(video) && slideAllowsPlay(video)) tryPlay(video);
    setTimeout(function () { ensurePlaying(video, attempt + 1); }, 600);
  }

  // Danach dauerhaft: wer im Blick ist, soll auch laufen. Gedeckelt, damit
  // daraus kein Schlagabtausch mit dem Theme wird.
  function keepPlaying(video) {
    if (video.dataset.vpKeepPlaying) return;
    video.dataset.vpKeepPlaying = '1';
    var resumes = 0;
    video.addEventListener('pause', function () {
      if (resumes >= 20) return;
      setTimeout(function () {
        if (!video.paused || !inView(video) || !slideAllowsPlay(video)) return;
        resumes++;
        tryPlay(video);
      }, 250);
    });
  }

  function start(video) {
    // Laden sofort — das ist der eigentliche Zeitgewinn.
    video.preload = 'auto';
    try { video.load(); } catch (e) { /* egal, global.js ruft ohnehin play() */ }
    // Abspielen erst, wenn das Theme sich sortiert hat.
    whenDomReady(function () {
      setTimeout(function () {
        keepPlaying(video);
        ensurePlaying(video, 0);
      }, 60);
    });
  }

  // Mobil eine leichtere Quelle setzen; ruft done() in jedem Fall genau einmal.
  function chooseSource(video, fullBleed, done) {
    var mp4 = firstMp4(video);
    if (!mp4 || !isMobile()) return done();

    var playlist = deriveM3u8(mp4);
    if (!playlist) return done();

    if (!window.fetch) return done();

    // Sicherheitsnetz: sollte die gewaehlte Stufe nicht abspielbar sein,
    // zurueck auf die Quelle des Themes.
    video.addEventListener('error', function fallback() {
      video.removeEventListener('error', fallback);
      if (video.getAttribute('src') === mp4) return;
      video.src = mp4;
      start(video);
    });

    var settled = false;
    var finish = function () { if (!settled) { settled = true; done(); } };
    // Notbremse, falls der Abruf haengt: nach 2s mit der Theme-Quelle weiter.
    setTimeout(finish, 2000);

    fetch(playlist, { credentials: 'omit' })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
      .then(function (text) {
        var tag = pickRendition(text, fullBleed ? 720 : 480);
        var url = tag && swapRendition(mp4, tag);
        if (url && !settled) video.src = url;
        finish();
      })
      .catch(finish);
  }

  function upgrade(video) {
    if (!video || video.dataset[DONE]) return;
    var figure = video.closest('.lazy-video');
    if (!figure) return;
    if (!video.querySelector('source') && !video.getAttribute('src')) return; // noch nicht geparst
    video.dataset[DONE] = '1';

    sharpenPoster(video);

    video.muted = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    // Zustand des Themes frueher setzen: base.css haengt daran.
    video.addEventListener('loadedmetadata', function () {
      figure.classList.add('lazyloaded');
    });

    var fullBleed = figure.classList.contains('lazy-image--absolut');
    // "Medien mit Tabs" haengt seine Videos in .none-autoplay; global.js
    // laesst sie bewusst stehen. Quelle setzen ja, von sich aus starten nein.
    var noAutoplay = !!video.closest('.none-autoplay');

    chooseSource(video, fullBleed, function () {
      if (noAutoplay) { video.preload = 'metadata'; return; }

      if (eagerUsed < EAGER_LIMIT) {
        eagerUsed++;
        start(video);
        return;
      }

      video.preload = 'metadata';
      if (!window.IntersectionObserver) { start(video); return; }
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          io.disconnect();
          start(video);
        }
      }, { rootMargin: '0px 0px 800px 0px' });
      io.observe(figure);
    });
  }

  // Der Parser haengt <source> erst nach <video> ein — deshalb einen Task
  // spaeter zugreifen und am Ende des Parsens noch einmal durchgehen.
  function queue(video) {
    if (!video || video.dataset[DONE]) return;
    setTimeout(function () { upgrade(video); }, 0);
  }

  function sweep(root) {
    var list = (root || document).querySelectorAll('.lazy-video video');
    for (var i = 0; i < list.length; i++) upgrade(list[i]);
  }

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'VIDEO') queue(node);
        else if (node.querySelector && node.querySelector('video')) {
          var inner = node.querySelectorAll('video');
          for (var k = 0; k < inner.length; k++) queue(inner[k]);
        }
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { sweep(); });
  } else {
    sweep();
  }

  // Theme-Editor: nachgeladene Sektionen ebenfalls behandeln.
  document.addEventListener('shopify:section:load', function (e) {
    sweep(e.target || document);
  });
})();
